import React from 'react';
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import {
  useCongregationMeQuery,
  useCongregationMastersQuery,
} from '../hooks/useCongregationQueries';
import type {
  CongregationMastersPayload,
  CongregationMeResponse,
  CongregationPersona,
  CongregationPersonaOption,
} from '../types/congregation';
import type { ApiError } from '../api';
import {
  deriveAvailablePersonas,
  getPersonaRoute,
  loadStoredPersona,
  persistPersona,
  selectDefaultPersona,
} from '../utils/congregation';
import { congregationKeys } from '../query/keys';
import { trackAnalyticsEvent } from '../utils/analytics';
import { flags } from '../config/flags';
interface CongregationContextValue {
  meQuery: UseQueryResult<CongregationMeResponse, ApiError>;
  mastersQuery: UseQueryResult<CongregationMastersPayload, ApiError>;
  membership: CongregationMeResponse;
  membershipReady: boolean;
  personaOptions: CongregationPersonaOption[];
  activePersona: CongregationPersona;
  setActivePersona: (persona: CongregationPersona) => void;
  getRouteForPersona: (persona: CongregationPersona) => string;
}

const CongregationContext = React.createContext<CongregationContextValue | undefined>(undefined);

export const CongregationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const meQuery = useCongregationMeQuery();
  const queryClient = useQueryClient();
  const membershipFallback: CongregationMeResponse | undefined = React.useMemo(() => {
    const status = meQuery.error?.status;
    if (status && (status === 403 || status === 404)) {
      return { member: null, roles: [] };
    }
    return undefined;
  }, [meQuery.error]);

  const resolvedMembership = meQuery.data ?? membershipFallback ?? { member: null, roles: [] };
  const membershipReady = meQuery.isSuccess || Boolean(membershipFallback);

  const personaOptions = React.useMemo(() => {
    const options = deriveAvailablePersonas(resolvedMembership);
    if (!flags.congregationMentorStage0) {
      return options.filter((option) => option.key !== 'MENTOR');
    }
    return options;
  }, [resolvedMembership]);

  const storedPersonaRef = React.useRef<CongregationPersona | null>(loadStoredPersona());
  const [activePersona, setActivePersonaState] = React.useState<CongregationPersona>(() =>
    selectDefaultPersona(personaOptions, storedPersonaRef.current),
  );
  const previousPersonaRef = React.useRef<CongregationPersona | null>(null);
  const skipTrackingRef = React.useRef(false);

  React.useEffect(() => {
    setActivePersonaState((current) => {
      if (personaOptions.some((option) => option.key === current)) {
        return current;
      }

      const stored = storedPersonaRef.current;
      if (stored && personaOptions.some((option) => option.key === stored)) {
        storedPersonaRef.current = stored;
        persistPersona(stored);
        return stored;
      }

      const fallback = selectDefaultPersona(personaOptions);
      storedPersonaRef.current = fallback;
      persistPersona(fallback);
      return fallback;
    });
  }, [personaOptions]);

  React.useEffect(() => {
    const hasMentorPersona = personaOptions.some((option) => option.key === 'MENTOR');
    if (hasMentorPersona) {
      return;
    }

    const predicate = (query: { queryKey: unknown }) => {
      if (!Array.isArray(query.queryKey)) return false;
      const [namespace, resource] = query.queryKey as [unknown, unknown];
      if (namespace !== congregationKeys.root[0]) return false;
      return (
        resource === 'mentor-applications' ||
        resource === 'birthdays' ||
        resource === 'anniversaries' ||
        resource === 'masters'
      );
    };

    queryClient.cancelQueries({ predicate });
    queryClient.removeQueries({ predicate });
  }, [personaOptions, queryClient]);

  React.useEffect(() => {
    persistPersona(activePersona);
    if (skipTrackingRef.current) {
      skipTrackingRef.current = false;
      previousPersonaRef.current = activePersona;
      return;
    }

    if (previousPersonaRef.current) {
      trackAnalyticsEvent('congregation.persona.selected', {
        from: previousPersonaRef.current,
        to: activePersona,
      });
    } else {
      trackAnalyticsEvent('congregation.persona.initialised', { persona: activePersona });
    }

    previousPersonaRef.current = activePersona;
  }, [activePersona]);

  const hasMentorRole = React.useMemo(
    () => (resolvedMembership.roles ?? []).some((role) => {
      const normalizedRole = role.role?.toUpperCase();
      return normalizedRole === 'MENTOR' || normalizedRole === 'FACILITATOR';
    }),
    [resolvedMembership.roles],
  );
  const hasMemberProfile = Boolean(resolvedMembership.member);

  const shouldEnableMasters =
    membershipReady &&
    flags.congregationMentorStage0;

  React.useEffect(() => {
    if (!membershipReady) {
      return;
    }

    if (!hasMentorRole || hasMemberProfile) {
      return;
    }

    queryClient.cancelQueries({ queryKey: congregationKeys.masters() });
    queryClient.removeQueries({ queryKey: congregationKeys.masters() });
  }, [membershipReady, hasMentorRole, hasMemberProfile, queryClient]);

  const mastersQuery = useCongregationMastersQuery({
    enabled: shouldEnableMasters,
  });

  const setActivePersona = React.useCallback((persona: CongregationPersona) => {
    if (previousPersonaRef.current === persona) {
      return;
    }
    storedPersonaRef.current = persona;
    setActivePersonaState(persona);
    persistPersona(persona);
    trackAnalyticsEvent('congregation.persona.selected', {
      from: previousPersonaRef.current,
      to: persona,
    });
    previousPersonaRef.current = persona;
    skipTrackingRef.current = true;
  }, []);

  const getRouteForPersona = React.useCallback((persona: CongregationPersona) => getPersonaRoute(persona), []);

  const value = React.useMemo<CongregationContextValue>(
    () => ({
      meQuery,
      mastersQuery,
      membership: resolvedMembership,
      membershipReady,
      personaOptions,
      activePersona,
      setActivePersona,
      getRouteForPersona,
    }),
    [
      meQuery,
      mastersQuery,
      resolvedMembership,
      membershipReady,
      personaOptions,
      activePersona,
      setActivePersona,
      getRouteForPersona,
    ],
  );

  return <CongregationContext.Provider value={value}>{children}</CongregationContext.Provider>;
};

export const useCongregationContext = (): CongregationContextValue => {
  const context = React.useContext(CongregationContext);
  if (!context) {
    throw new Error('useCongregationContext must be used within a CongregationProvider');
  }
  return context;
};
